import React from "react";
import classNames from "classnames";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { Button } from "@design-system/Button/Button";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { RELATIONSHIP_FILTER_LABELS } from "./ownershipGraph.helpers";
import {
    OWNERSHIP_SEARCH_DEBOUNCE_TIME,
    OWNERSHIP_SEARCH_MIN_LENGTH,
    getOwnershipToolbarActions,
} from "./ownershipToolbar.helpers";
import {
    ALL_RELATIONSHIPS,
    OwnershipEntityKindFilter,
    RelationshipFilter,
} from "./ownership.types";
import styles from "./OwnershipToolbar.module.scss";

const ENTITY_KIND_LABELS: Record<OwnershipEntityKindFilter, string> = {
    all: "All entity types",
    business: "Businesses",
    personal: "Owners",
};

const RELATIONSHIP_FILTER_OPTIONS = [
    ConsolidationEntityRelationshipType.ownership,
    ConsolidationEntityRelationshipType.loan,
    ConsolidationEntityRelationshipType.other,
] satisfies RelationshipFilter[];

const RELATIONSHIP_FILTER_ITEMS: Array<PickerItemData<RelationshipFilter>> = [
    {
        value: ALL_RELATIONSHIPS,
        label: RELATIONSHIP_FILTER_LABELS[ALL_RELATIONSHIPS],
    },
    ...RELATIONSHIP_FILTER_OPTIONS.map((filter) => ({
        value: filter,
        label: RELATIONSHIP_FILTER_LABELS[filter],
    })),
];

const ENTITY_KIND_ITEMS: Array<PickerItemData<OwnershipEntityKindFilter>> = (
    ["all", "business", "personal"] satisfies OwnershipEntityKindFilter[]
).map((kind) => ({ value: kind, label: ENTITY_KIND_LABELS[kind] }));

interface OwnershipToolbarProps {
    search: string;
    onSearchChange: (search: string) => void;
    relationshipTypeFilter: RelationshipFilter;
    onRelationshipTypeFilterChange: (filter: RelationshipFilter) => void;
    entityKindFilter: OwnershipEntityKindFilter;
    onEntityKindFilterChange: (filter: OwnershipEntityKindFilter) => void;
    hasConsolidationGroups: boolean;
    onAddConsolidationGroup: () => void;
    onAddEntity: () => void;
}

export const OwnershipToolbar: React.FC<OwnershipToolbarProps> = ({
    search,
    onSearchChange,
    relationshipTypeFilter,
    onRelationshipTypeFilterChange,
    entityKindFilter,
    onEntityKindFilterChange,
    hasConsolidationGroups,
    onAddConsolidationGroup,
    onAddEntity,
}) => (
    <TablePageHeader
        search={search}
        onSearchChange={onSearchChange}
        searchMinLength={OWNERSHIP_SEARCH_MIN_LENGTH}
        searchDebounceTime={OWNERSHIP_SEARCH_DEBOUNCE_TIME}
        isFullPage
        borderBottom
    >
        <div className={styles.filters}>
            <div className={styles.selectGroup}>
                <Picker.Select
                    items={RELATIONSHIP_FILTER_ITEMS}
                    value={relationshipTypeFilter}
                    onValueChange={(value) =>
                        onRelationshipTypeFilterChange(
                            value ?? ALL_RELATIONSHIPS,
                        )
                    }
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={classNames(
                                styles.toolbarControl,
                                styles.selectGroupLeft,
                            )}
                        />
                    }
                />
                <Picker.Select
                    items={ENTITY_KIND_ITEMS}
                    value={entityKindFilter}
                    onValueChange={(value) =>
                        onEntityKindFilterChange(value ?? "all")
                    }
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={classNames(
                                styles.toolbarControl,
                                styles.selectGroupRight,
                            )}
                        />
                    }
                />
            </div>
            {getOwnershipToolbarActions({
                hasConsolidationGroups,
                onAddConsolidationGroup,
                onAddEntity,
            }).map((action) => (
                <Button
                    key={action.key}
                    variant="primary"
                    size="md"
                    className={styles.toolbarControl}
                    onClick={action.onClick}
                >
                    {action.label}
                </Button>
            ))}
        </div>
    </TablePageHeader>
);
